import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { Layout } from "../components/layout/Layout";
import { getUserType, isAuthenticated, hasRole, clearSession, getToken } from "../lib/auth";
import { getCurrentUser } from "../lib/api";
import { OnflyLoading } from "../tools/OnflyLoading";
import HomePage from "../pages/HomePage";
import { LoginPage } from "../pages/login/LoginPage";
import { GoogleCallbackPage } from "../pages/login/GoogleCallbackPage";
import OnboardingDashboardPage from "../pages/rh/onboarding/OnboardingDashboardPage.tsx";
import CyberUploadPage from "../pages/cyber/CyberUploadPage.tsx";
import MissionsAdminPage from "../pages/admin/missions/MissionsAdminPage";
import { PermissionsAdminPage } from "../pages/admin/permissions/PermissionsAdminPage";
import { UsersAdminPage } from "../pages/admin/users/UsersAdminPage";
import { RolesAdminPage } from "../pages/admin/roles/RolesAdminPage";
import TicketMappingsPage from "../pages/admin/ticket-mappings/TicketMappingsPage.tsx";
import { LeadersAdminPage } from "../pages/admin/leaders/LeadersAdminPage";
import ProcessHubFormsPage from "../pages/process-hub/forms/ProcessHubFormsPage";
import HrProcessesPage from "../pages/rh/processes/HrProcessesPage";
import { MetricsPage } from "../pages/metrics/MetricsPage";

// Candidato migrou para o portal próprio (starter-pack). O GG Hub é interno:
// só colaborador loga aqui, então o "home" é sempre a raiz interna.
const homePathFor = (_userType: string | null) => "/";

const OldPathRedirect = ({ to }: { to: string }) => {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`${to}${qs ? `?${qs}` : ""}`} replace />;
};

// Sessão já confirmada com o backend nesta aba — evita revalidar em cada
// troca de rota dentro da área autenticada (o gate raiz já fez a checagem).
let sessionVerifiedForToken: string | null = null;

const RequireAuth = ({
  children,
  allow,
  allowRoles,
  verify,
}: {
  children: React.ReactNode;
  allow?: string[];
  allowRoles?: string[];
  /** Confirma a sessão com o backend (GET /auth/me) antes de renderizar —
   *  sem isso, qualquer objeto escrito à mão no localStorage passa pelo
   *  guard client-side. Usar só no gate raiz (uma vez por sessão). */
  verify?: boolean;
}) => {
  const location = useLocation();
  const token = getToken();
  const needsVerification = verify && !!token && sessionVerifiedForToken !== token;
  const [status, setStatus] = useState<"checking" | "done">(needsVerification ? "checking" : "done");

  useEffect(() => {
    if (!needsVerification || !token) return;
    let cancelled = false;
    getCurrentUser(token)
      .then(() => {
        if (cancelled) return;
        sessionVerifiedForToken = token;
        setStatus("done");
      })
      .catch(() => {
        if (cancelled) return;
        clearSession();
        setStatus("done");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsVerification, token]);

  if (!isAuthenticated()) {
    sessionStorage.setItem("gg_redirect_after_login", location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (needsVerification && status === "checking") {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <OnflyLoading containerSize={40} />
      </div>
    );
  }

  // A checagem acima pode ter invalidado a sessão (token falso/expirado).
  if (!isAuthenticated()) {
    sessionStorage.setItem("gg_redirect_after_login", location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  const userType = getUserType();

  if (!userType) {
    return <Navigate to="/login" replace />;
  }

  if (allow && !allow.includes(userType)) {
    return <Navigate to={homePathFor(userType)} replace />;
  }

  if (allowRoles && !allowRoles.some((role) => hasRole(role))) {
    return <Navigate to={homePathFor(userType)} replace />;
  }

  return <>{children}</>;
};

const RedirectIfAuth = ({ children }: { children: ReactNode }) => {
  if (isAuthenticated()) {
    const userType = getUserType();
    if (!userType) {
      clearSession();
      return <>{children}</>;
    }
    return <Navigate to={homePathFor(userType)} replace />;
  }
  return <>{children}</>;
};

const router = Sentry.wrapCreateBrowserRouter(createBrowserRouter)([
  {
    path: "/login",
    element: (
      <RedirectIfAuth>
        <LoginPage />
      </RedirectIfAuth>
    ),
  },
  {
    path: "/auth/google/callback",
    element: <GoogleCallbackPage />,
  },
  {
    path: "/",
    element: (
      <RequireAuth allow={["collaborator"]} verify>
        <Layout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomePage /> },
      { path: "internal", element: <HomePage /> },
      { path: "internal/home", element: <HomePage /> },
      { path: "internal/candidates/monitoring", element: <OnboardingDashboardPage /> },
      { path: "rh/onboarding", element: <OldPathRedirect to="/internal/candidates/monitoring" /> },
      { path: "internal/cyber/access", element: <CyberUploadPage /> },
      { path: "internal/admissional/map", element: <MissionsAdminPage /> },
      { path: "internal/rh/processes", element: <HrProcessesPage /> },
      { path: "internal/metrics", element: <MetricsPage /> },
      {
        path: "internal/admin/users",
        element: (
          <RequireAuth allowRoles={["admin"]}>
            <UsersAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: "internal/admin/roles",
        element: (
          <RequireAuth allowRoles={["admin"]}>
            <RolesAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: "internal/admin/permissions",
        element: (
          <RequireAuth allowRoles={["admin"]}>
            <PermissionsAdminPage />
          </RequireAuth>
        ),
      },
      {
        path: "internal/admin/ticket-mappings",
        element: (
          <RequireAuth allowRoles={["admin"]}>
            <TicketMappingsPage />
          </RequireAuth>
        ),
      },
      { path: "internal/process-hub/forms", element: <ProcessHubFormsPage /> },
      {
        path: "internal/admin/leaders",
        element: (
          <RequireAuth allowRoles={["admin"]}>
            <LeadersAdminPage />
          </RequireAuth>
        ),
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
